import { useState } from "react";
import axios from "axios";
import { MessageSquareHeart, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeedbackPrompt = () => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(() => localStorage.getItem("doclyx_feedback_done") === "1");
  const [quote, setQuote] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  if (done) {
    return (
      <p className="mt-3 pt-3 border-t border-hairline font-mono text-[10px] uppercase tracking-[0.15em] text-mist flex items-center gap-1.5" data-testid="feedback-done">
        <Check className="w-3.5 h-3.5 text-azure" strokeWidth={1.5} /> Thanks — your words help other teams trust this
      </p>
    );
  }

  const submit = async () => {
    if (quote.trim().length < 10) {
      setError("A short sentence is plenty (10+ characters).");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await axios.post(`${API}/feedback`, { quote: quote.trim(), name: name.trim(), role: role.trim(), company: "" });
      localStorage.setItem("doclyx_feedback_done", "1");
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not send — try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-3 pt-3 border-t border-hairline flex items-center justify-between gap-3 flex-wrap" data-testid="feedback-prompt">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-fog">How was that?</p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="feedback-open-button"
          className="inline-flex items-center gap-1.5 border border-hairline rounded-full px-3.5 py-2 min-h-[44px] font-mono text-[10px] uppercase tracking-[0.15em] text-azure hover:text-ink hover:border-ink transition-colors duration-300"
        >
          <MessageSquareHeart className="w-3.5 h-3.5" strokeWidth={1.5} /> Share one line
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-hairline space-y-2" data-testid="feedback-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-fog">One honest line — reviewed before it appears on the site</p>
      <textarea
        value={quote}
        onChange={(e) => setQuote(e.target.value)}
        rows={2}
        maxLength={500}
        placeholder="What did you run, and how did it go?"
        data-testid="feedback-quote-input"
        className="w-full border border-hairline rounded-lg bg-page px-3.5 py-2.5 font-mono text-xs text-ink placeholder:text-mist focus:outline-none focus:border-azure transition-colors duration-300 resize-none"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          placeholder="Name (optional)"
          data-testid="feedback-name-input"
          className="border border-hairline rounded-lg bg-page px-3.5 py-2.5 font-mono text-xs text-ink placeholder:text-mist focus:outline-none focus:border-azure transition-colors duration-300"
        />
        <input
          value={role}
          onChange={(e) => setRole(e.target.value)}
          maxLength={60}
          placeholder="Role (optional)"
          data-testid="feedback-role-input"
          className="border border-hairline rounded-lg bg-page px-3.5 py-2.5 font-mono text-xs text-ink placeholder:text-mist focus:outline-none focus:border-azure transition-colors duration-300"
        />
      </div>
      {error && <p className="font-mono text-[10px] text-[#FF3333]" data-testid="feedback-error">{error}</p>}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={sending}
          data-testid="feedback-submit-button"
          className="inline-flex items-center gap-1.5 v4-btn-primary rounded-full px-4 min-h-[44px] font-mono text-[10px] uppercase tracking-[0.15em] disabled:opacity-40 transition-colors duration-300"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : null}
          Send
        </button>
      </div>
    </div>
  );
};
